import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import CommonModal from '../modals/CommonModal';
import ConfirmModal from '../modals/ConfirmModal';
import { connection } from '../../offline/connection';
import { PAPER_WIDTHS, drawerRolesBody, jobLine, normalizeDiscovery, normalizePrinters, printerStatus, printingExplain, rolesSourceText, rolesText, validateAddress } from '../../offline/printers';

const TONE = { ok: 'offOk', warn: 'offWarn', bad: 'offBad' };
const EMPTY = { capable: false, assumedRoles: false, printers: [], settings: { restaurantName: '', abn: '', phone: '', multiStation: 'auto' } };

// The engine's printer control routes (GET /_oa/printers, POST /_oa/printers {action}, GET /_oa/print-jobs, POST /_oa/print-retry|print-discard).
const act = (action, params = {}) => connection.call('POST', 'printers', { ...params, action });
const listPrinters = async () => {
  const r = await connection.call('GET', 'printers');
  return r && r.ok !== false && Array.isArray(r.printers) ? r : null;
};
const listJobs = async (state) => {
  const r = await connection.call('GET', `print-jobs?state=${encodeURIComponent(state)}&limit=50`);
  return r && Array.isArray(r.jobs) ? r : { pending: 0, failed: 0, jobs: [] };
};

/** A small dialog in the POS's own modal style. */
function Dialog({ isOpen, title, onClose, children, footer }) {
  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="printerDialog">
      <div className="modal-header">
        <h5 className="modal-title">{title}</h5>
        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-footer">{footer}</div>}
    </CommonModal>
  );
}

/** One printer: name, station, address, status and its actions. */
export function PrinterItem({ p, busy, onTest, onAddress, onForget, onDrawer, onCols, onClearRoles }) {
  const st = printerStatus(p);
  return (
    <li className="prnItem">
      <div className="prnInfo">
        <strong>{p.label}</strong>
        {p.primary && <small>Main printer</small>}
        <small>
          {rolesText(p)}
          {rolesSourceText(p.rolesSource) && ` · ${rolesSourceText(p.rolesSource)}`}
        </small>
        <small>{p.bound ? `Address ${p.ip}${p.port !== 9100 ? `:${p.port}` : ''}${p.identity ? ` · ${p.identity}` : ''}` : 'No address yet'}</small>
        <small className={TONE[st.tone]}>
          <b>{st.label}</b>
          {st.detail ? ` · ${st.detail}` : ''}
        </small>
      </div>
      <div className="offActions">
        <button type="button" className="btn btnPrimary" onClick={onTest} disabled={busy !== '' || !p.bound}>
          {busy === `test-${p.id}` ? 'Printing...' : 'Test print'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onAddress} disabled={busy !== ''}>
          {p.bound ? 'Change address' : 'Enter IP address'}
        </button>
        {p.bound && (
          <button type="button" className="btn btn-secondary" onClick={onForget} disabled={busy !== ''}>
            Forget address
          </button>
        )}
      </div>
      {p.bound && (
        <div className="offActions prnOptions">
          <label className="prnCheck">
            <input type="checkbox" checked={p.roles.drawer} disabled={busy !== ''} onChange={(e) => onDrawer(e.target.checked)} />
            <span>Opens the cash drawer</span>
          </label>
          <label className="prnSelect">
            <span>Paper</span>
            <select className="form-select" value={p.cols} disabled={busy !== ''} onChange={(e) => onCols(Number(e.target.value))}>
              {PAPER_WIDTHS.some((w) => w.cols === p.cols) ? null : <option value={p.cols}>{p.cols} columns</option>}
              {PAPER_WIDTHS.map((w) => (
                <option key={w.cols} value={w.cols}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>
          {p.rolesSource === 'manager' && (
            <button type="button" className="btn btn-secondary" onClick={onClearRoles} disabled={busy !== ''}>
              Use restaurant settings for stations
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/** One print job, with Retry / Discard while it can still be acted on. */
export function JobItem({ j, busy, onRetry, onDiscard }) {
  const line = jobLine(j);
  return (
    <li>
      <div className="prnInfo">
        <strong>{line.title}</strong>
        <small>{line.detail}</small>
      </div>
      {line.canAct && (
        <div className="offActions mt-0">
          <button type="button" className="btn btnPrimary" onClick={onRetry} disabled={busy !== ''}>
            Retry
          </button>
          <button type="button" className="btn btn-danger" onClick={onDiscard} disabled={busy !== ''}>
            Discard
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * Printers (part of the Offline mode page): the restaurant's printers with their station, address and status, "Find printers on the network", a
 * typed address, a test print, forgetting an address, cash drawer and paper width, receipt details, and the print jobs that failed or are waiting
 * (retry / discard). Everything goes through the engine's /_oa/ control API; the page renders this only when an engine is present.
 */
export default function PrintersSection({ offline }) {
  const [data, setData] = useState(EMPTY);
  const [jobs, setJobs] = useState({ pending: 0, failed: 0, jobs: [] });
  const [busy, setBusy] = useState('');
  const [addressFor, setAddressFor] = useState(null);
  const [ip, setIp] = useState('');
  const [port, setPort] = useState('');
  const [addressError, setAddressError] = useState('');
  const [found, setFound] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receipt, setReceipt] = useState({ restaurantName: '', abn: '', phone: '' });

  const reload = useCallback(async () => {
    const [p, j, w] = await Promise.all([listPrinters(), listJobs('failed'), listJobs('pending')]);
    if (p) setData(normalizePrinters(p));
    setJobs({ pending: w.pending, failed: j.failed, jobs: [...j.jobs, ...w.jobs.filter((x) => !j.jobs.some((y) => y.job_uid === x.job_uid))] });
  }, []);

  useEffect(() => {
    reload();
  }, [reload, offline]);

  const run = async (name, fn, okMessage) => {
    setBusy(name);
    try {
      const r = await fn();
      if (r?.ok === false) toast.error(r.message || 'That did not work');
      else toast.success(okMessage || r?.message || 'Done');
      return r;
    } finally {
      setBusy('');
      reload();
    }
  };

  const openAddress = (p, suggestion) => {
    setAddressFor(p);
    setIp(suggestion?.ip || p.ip || '');
    setPort(suggestion?.port && suggestion.port !== 9100 ? String(suggestion.port) : '');
    setAddressError('');
  };

  const submitAddress = async () => {
    const v = validateAddress(ip, port);
    if (!v.ok) return setAddressError(v.error);
    setAddressError('');
    const r = await run(`bind-${addressFor.id}`, () => act('bind', { printer_id: addressFor.id, ip: v.ip, port: v.port }));
    if (r?.ok) {
      setAddressFor(null);
      setFound(null);
    } else {
      setAddressError(r?.message || 'That did not work');
    }
  };

  const discover = async () => {
    setBusy('discover');
    try {
      const r = await act('discover');
      if (r?.ok === false) return toast.error(r.message || 'Search failed');
      setFound(normalizeDiscovery(r));
    } finally {
      setBusy('');
      reload();
    }
  };

  const openReceipt = () => {
    setReceipt({ restaurantName: data.settings.restaurantName, abn: data.settings.abn, phone: data.settings.phone });
    setReceiptOpen(true);
  };

  // Enter in the address fields saves (the on-screen keyboard can cover the Save button)
  const onEnter = (e) => {
    if (e.key === 'Enter' && busy === '') {
      e.preventDefault();
      submitAddress();
    }
  };

  const activePrinters = data.printers.filter((p) => p.active);
  const worry = jobs.jobs.length > 0;
  return (
    <>
      <section className="offCard" id="printers">
        <h3>Printers</h3>
        <p className="offNote">{printingExplain({ offline, capable: data.capable, printerCount: data.printers.length })}</p>
        {data.assumedRoles && data.printers.length > 0 && (
          <p className="offNote offWarn">The restaurant settings give no printer a station yet, so this terminal assumes: every printer takes kitchen dockets and the main printer takes receipts.</p>
        )}
        <div className="offActions">
          <button type="button" className="btn btnPrimary" onClick={discover} disabled={busy !== ''}>
            {busy === 'discover' ? 'Searching...' : 'Find printers on the network'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => run('probe', () => act('probe'), 'Printers checked')} disabled={busy !== ''}>
            {busy === 'probe' ? 'Checking...' : 'Check now'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={openReceipt} disabled={busy !== ''}>
            Receipt details
          </button>
        </div>
        <p className="offNote">The cash drawer and paper width are saved on this terminal only.</p>
        {data.printers.length === 0 && <p className="offNote">No printers are listed for this restaurant. They come from the cloud printer settings; refresh the offline data while online.</p>}
        <ul className="offList">
          {data.printers.map((p) => (
            <PrinterItem
              key={p.id}
              p={p}
              busy={busy}
              onTest={() => run(`test-${p.id}`, () => act('test', { printer_id: p.id }))}
              onAddress={() => openAddress(p)}
              onForget={() => setConfirm({ kind: 'forget', printer: p })}
              onDrawer={(on) => run(`drawer-${p.id}`, () => act('roles', drawerRolesBody(p, on)), on ? 'Cash drawer on' : 'Cash drawer off')}
              onCols={(cols) => run(`cols-${p.id}`, () => act('cols', { printer_id: p.id, cols }), 'Paper width saved')}
              onClearRoles={() => run(`clear-${p.id}`, () => act('roles', { printer_id: p.id, clear: true }), 'Using the restaurant settings again')}
            />
          ))}
        </ul>
      </section>

      <section className="offCard" id="print-jobs">
        <h3>Print jobs</h3>
        <p className="offNote">
          {worry ? `${jobs.failed} failed or needing attention · ${jobs.pending} waiting to print.` : 'Nothing is waiting and nothing has failed.'} Dockets for orders taken offline wait here until their printer answers.
        </p>
        <ul className="offList">
          {jobs.jobs.map((j) => (
            <JobItem
              key={j.job_uid}
              j={j}
              busy={busy}
              onRetry={() => run(`retry-${j.job_uid}`, () => connection.call('POST', 'print-retry', { job_uid: j.job_uid }), 'Sent to the printer again')}
              onDiscard={() => setConfirm({ kind: 'discard', job: j, title: jobLine(j).title })}
            />
          ))}
        </ul>
      </section>

      <Dialog
        isOpen={addressFor !== null}
        title={addressFor ? `Address for ${addressFor.label}` : 'Printer address'}
        onClose={() => (busy === '' ? setAddressFor(null) : undefined)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setAddressFor(null)} disabled={busy !== ''}>
              Cancel
            </button>
            <button type="button" className="btn btnPrimary" onClick={submitAddress} disabled={busy !== ''}>
              {busy.startsWith('bind') ? 'Checking...' : 'Save address'}
            </button>
          </>
        }
      >
        <label className="d-block mb-2">
          <span className="d-block mb-1">IP address</span>
          <input className={`form-control ${addressError ? 'is-invalid' : ''}`} value={ip} onChange={(e) => setIp(e.target.value)} onKeyDown={onEnter} placeholder="192.168.1.50" inputMode="decimal" autoFocus />
        </label>
        <label className="d-block mb-2">
          <span className="d-block mb-1">Port (leave empty for 9100)</span>
          <input className="form-control" value={port} onChange={(e) => setPort(e.target.value)} onKeyDown={onEnter} placeholder="9100" inputMode="numeric" maxLength={5} />
        </label>
        {addressError && (
          <p className="offNote offBad" role="alert">
            {addressError}
          </p>
        )}
        <p className="offNote">The terminal checks that something there accepts printing, and refuses an address that belongs to a different printer.</p>
      </Dialog>

      <Dialog isOpen={found !== null && addressFor === null} title="Printers on the network" onClose={() => setFound(null)}>
        {found && (
          <>
            <p className="offNote">{found.message}</p>
            {found.bound.length > 0 && (
              <ul className="offList">
                {found.bound.map((b) => (
                  <li key={b.printerId}>
                    <strong>{b.label}</strong>
                    <small className="offOk">Matched and saved: {b.ip}</small>
                  </li>
                ))}
              </ul>
            )}
            {found.unmatched.length > 0 && <p className="offNote">Found, but not matched to a printer from the restaurant settings. Pick which one it is:</p>}
            <ul className="offList">
              {found.unmatched.map((u) => (
                <li key={`${u.ip}:${u.port}`}>
                  <div className="prnInfo">
                    <strong>{u.ip}</strong>
                    <small>
                      {u.identity || 'Does not name itself'} · found by {u.via === 'mdns' ? 'its network announcement' : 'scanning the Wi-Fi'}
                    </small>
                  </div>
                  <div className="offActions mt-0">
                    {activePrinters.map((p) => (
                      <button key={p.id} type="button" className="btn btn-secondary" onClick={() => openAddress(p, u)} disabled={busy !== ''}>
                        Use for {p.label}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
            {found.bound.length === 0 && found.unmatched.length === 0 && <p className="offNote">No printers answered. Check they are on and on the same Wi-Fi as this terminal, or enter an address by hand.</p>}
          </>
        )}
      </Dialog>

      <Dialog
        isOpen={receiptOpen}
        title="Receipt details"
        onClose={() => setReceiptOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setReceiptOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btnPrimary"
              disabled={busy !== ''}
              onClick={async () => {
                const r = await run('settings', () => act('settings', { restaurant_name: receipt.restaurantName, abn: receipt.abn, phone: receipt.phone }), 'Receipt details saved');
                if (r?.ok) setReceiptOpen(false);
              }}
            >
              Save
            </button>
          </>
        }
      >
        <p className="offNote">Printed on receipts made offline. The restaurant name comes from the login; the ABN and phone are not available offline unless you type them here.</p>
        {[['restaurantName', 'Restaurant name'], ['abn', 'ABN'], ['phone', 'Phone']].map(([key, label]) => (
          <label className="d-block mb-2" key={key}>
            <span className="d-block mb-1">{label}</span>
            <input className="form-control" value={receipt[key]} maxLength={key === 'restaurantName' ? 60 : 20} onChange={(e) => setReceipt({ ...receipt, [key]: e.target.value })} />
          </label>
        ))}
      </Dialog>

      <ConfirmModal
        isOpen={confirm?.kind === 'forget'}
        title="Forget this address?"
        message={`${confirm?.printer?.label || 'This printer'} will not print offline until it has an address again. The restaurant's printer settings are not changed.`}
        confirmText="Forget address"
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const p = confirm.printer;
          setConfirm(null);
          await run('unbind', () => act('unbind', { printer_id: p.id }), 'Address forgotten');
        }}
      />
      <ConfirmModal
        isOpen={confirm?.kind === 'discard'}
        title="Discard this print job?"
        message={`${confirm?.title || 'This docket'} will not be printed. Retry instead if you are not sure.`}
        confirmText="Discard"
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const uid = confirm.job.job_uid;
          setConfirm(null);
          await run('discard-job', () => connection.call('POST', 'print-discard', { job_uid: uid }), 'Discarded');
        }}
      />
    </>
  );
}
